/**
 * @voltxampere/audio — AudioContext lazy-init wrapper (Sprint 31)
 *
 * Browser autoplay policy compliance: AudioContext must be created /
 * started inside a user gesture handler (click, keydown, F5, …). This
 * module wraps Tone.js's `Tone.start()` in an idempotent helper so
 * any caller can `await ensureAudioContext()` without worrying about
 * double-init or pre-gesture invocation.
 *
 * Browser matrix (verified Sprint 31):
 *   - Chrome 90+: gesture required for `AudioContext.resume()`
 *   - Safari 14+: gesture required, iOS extra strict (per-page-load tap)
 *   - Firefox 80+: gesture required
 *   - Tone.js v14: `Tone.start()` returns Promise that resolves once
 *     the underlying AudioContext.state === "running"
 *
 * Sprint 31 boot path: AudioContext is NOT initialised on page load.
 * The first user gesture that produces sound (Run/F5/AYARLAR volume
 * slider drag) calls `ensureAudioContext()`. apps/web shows a one-time
 * info chip ("Sesi etkinleştirmek için tıklayın") for users who haven't
 * interacted yet.
 *
 * Tone.js dynamic import: matches the pattern in `engines/tone-engine.ts`
 * — module-level `import * as Tone from "tone"` causes jsdom test
 * environments to fail at module-resolution time. Dynamic import keeps
 * the audio package importable from Node-only contexts.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
let initialized = false;
let initPromise = null;
/**
 * Initialize AudioContext on first user gesture. Returns a Promise that
 * resolves once Tone.js is ready to play.
 *
 * Safe to call multiple times — subsequent calls return the same
 * resolved Promise so the caller can `await` without branching.
 */
export async function ensureAudioContext() {
    if (initialized)
        return;
    if (initPromise) {
        await initPromise;
        return;
    }
    initPromise = (async () => {
        const Tone = (await import("tone"));
        await Tone.start();
        initialized = true;
    })();
    await initPromise;
}
/** True iff `ensureAudioContext()` has resolved at least once. */
export function isAudioContextReady() {
    return initialized;
}
/**
 * Reset internal state. TEST-ONLY — Tone.js cannot tear down its
 * AudioContext in production browsers; this helper exists so unit
 * tests can simulate the pre-gesture state without leaking module-
 * level state across `it()` blocks.
 */
export function resetAudioContext() {
    initialized = false;
    initPromise = null;
}
//# sourceMappingURL=context.js.map